import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Plus, X, Search, ArrowRight, AlertTriangle, RefreshCw, Trash2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Modal, Button, FormField, Input, Select, PageHeader, Badge } from '../../components/UI';
import ConfirmModal from '../../components/ConfirmModal';
import Breadcrumb from '../../components/Breadcrumb';
import { purchaseRequestService, supplierService, medicineService } from '../../api/services';
import { toJalali } from '../../utils/jalali';
import toast from 'react-hot-toast';

const UNITS = ['Piece','Strip','Pack','Box','Carton','Bottle','Tube','Tablet','Capsule','Ampule','Vial','Sachet'];

function SupplierLookup({ selected, onSelect, onClear }) {
  const [q,setQ]=useState('');const [res,setRes]=useState([]);const [open,setOpen]=useState(false);
  const ref=useRef(null);const tmr=useRef(null);
  useEffect(()=>{const h=(e)=>{if(ref.current&&!ref.current.contains(e.target))setOpen(false);};document.addEventListener('mousedown',h);return()=>document.removeEventListener('mousedown',h);},[]);
  const doSearch=async(v)=>{if(!v)return;try{const r=await supplierService.search(v);setRes(r.data.data||[]);setOpen(true);}catch{}};
  if(selected)return(<div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200"><div><p className="text-sm font-semibold text-blue-800">{selected.name}</p><p className="text-xs text-blue-500">{selected.company}</p></div><button type="button" onClick={onClear}><X size={16} className="text-blue-300"/></button></div>);
  return(<div className="relative" ref={ref}><div className="relative"><Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/><input value={q} onChange={e=>{setQ(e.target.value);clearTimeout(tmr.current);tmr.current=setTimeout(()=>doSearch(e.target.value),280);}} placeholder="Search supplier..." className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"/></div>{open&&res.length>0&&(<div className="absolute top-full left-0 right-0 z-20 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl max-h-48 overflow-y-auto">{res.map(s=><button key={s._id} type="button" onClick={()=>{onSelect(s);setQ('');setRes([]);setOpen(false);}} className="w-full text-left px-4 py-3 hover:bg-blue-50 border-b border-gray-50 last:border-0"><p className="text-sm font-medium text-gray-800">{s.name}</p><p className="text-xs text-gray-400">{s.company}</p></button>)}</div>)}</div>);
}

function MedicineLookupInline({ onAdd }) {
  const [q,setQ]=useState('');const [res,setRes]=useState([]);const [open,setOpen]=useState(false);
  const ref=useRef(null);const tmr=useRef(null);
  useEffect(()=>{const h=(e)=>{if(ref.current&&!ref.current.contains(e.target))setOpen(false);};document.addEventListener('mousedown',h);return()=>document.removeEventListener('mousedown',h);},[]);
  const doSearch=useCallback(async(v)=>{if(!v){setRes([]);return;}try{const r=await medicineService.search(v);setRes(r.data.data||[]);setOpen(true);}catch{}},[]);
  return(<div className="relative" ref={ref}><div className="relative"><Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/><input value={q} onChange={e=>{setQ(e.target.value);clearTimeout(tmr.current);tmr.current=setTimeout(()=>doSearch(e.target.value),280);}} placeholder="Search to add medicine..." className="w-full pl-8 pr-3 py-2 text-sm border border-dashed border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-blue-50/40"/></div>{open&&res.length>0&&(<div className="absolute top-full left-0 right-0 z-30 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl max-h-48 overflow-y-auto">{res.map(m=><button key={m._id} type="button" onClick={()=>{onAdd(m);setQ('');setRes([]);setOpen(false);}} className="w-full text-left px-4 py-2.5 hover:bg-blue-50 border-b border-gray-50 last:border-0"><p className="text-sm font-medium text-gray-800">{m.commercialName}</p><p className="text-xs text-gray-400">{m.genericName}{m.strength?` · ${m.strength}`:''}{m.drugForm?` · ${m.drugForm}`:''}</p></button>)}</div>)}</div>);
}

const statusColor = { draft:'gray', pending:'yellow', converted:'green', cancelled:'red' };

export default function PurchaseRequestsPage() {
  const [data,setData]=useState([]);const [total,setTotal]=useState(0);const [loading,setLoading]=useState(false);
  const [page,setPage]=useState(1);const [fStatus,setFS]=useState('');
  const [outOfStock,setOOS]=useState([]);const [oosLoading,setOOSL]=useState(false);
  const [formOpen,setFormOpen]=useState(false);
  const [convertTarget,setCT]=useState(null);const [cancelTarget,setCAT]=useState(null);
  const [selectedSupplier,setSS]=useState(null);
  const [saving,setSaving]=useState(false);const [converting,setConv]=useState(false);const [cancelling,setCanc]=useState(false);
  const [items,setItems]=useState([{medicineName:'',medicineId:'',requestedQty:1,unit:'Piece',notes:''}]);

  const {register,handleSubmit,reset,watch}=useForm({defaultValues:{priority:'medium',notes:''}});

  const fetchData=async()=>{setLoading(true);try{const p={page,limit:20};if(fStatus)p.status=fStatus;const r=await purchaseRequestService.getAll(p);setData(r.data.data||[]);setTotal(r.data.pagination?.total||0);}catch{toast.error('Failed');}finally{setLoading(false);}};
  const fetchOOS=async()=>{setOOSL(true);try{const r=await purchaseRequestService.getOutOfStock();setOOS(r.data.data||[]);}catch{}finally{setOOSL(false);}};

  useEffect(()=>{fetchData();},[page,fStatus]); // eslint-disable-line
  useEffect(()=>{fetchOOS();},[]);

  const addItem=()=>setItems(p=>[...p,{medicineName:'',medicineId:'',requestedQty:1,unit:'Piece',notes:''}]);
  const removeItem=(i)=>setItems(p=>p.filter((_,j)=>j!==i));
  const updateItem=(i,f,v)=>setItems(p=>p.map((it,j)=>j===i?{...it,[f]:v}:it));
  const addFromSearch=(med)=>setItems(p=>[...p,{medicineName:med.commercialName,medicineId:med._id,requestedQty:1,unit:med.baseUnit||'Piece',notes:''}]);

  const addOOSToRequest=()=>{
    const its=outOfStock.slice(0,20).map(i=>({medicineName:i.medicine?.commercialName||'Unknown',medicineId:i.medicine?._id||'',requestedQty:Math.max(1,i.medicine?.reorderLevel||10),unit:i.medicine?.baseUnit||'Piece',isAutoAdded:true,notes:'Auto: out of stock'}));
    setItems(its); reset({priority:'high',notes:'Auto-generated from out-of-stock'});
    setFormOpen(true);
  };

  const onSubmit=async(data)=>{
    const validItems=items.filter(i=>i.medicineName.trim());
    if(!validItems.length)return toast.error('Add at least one medicine');
    setSaving(true);
    try{
      await purchaseRequestService.create({...data,items:validItems});
      toast.success('Request created');setFormOpen(false);setItems([{medicineName:'',medicineId:'',requestedQty:1,unit:'Piece',notes:''}]);reset();fetchData();
    }catch(e){toast.error(e.response?.data?.message||'Failed');}finally{setSaving(false);}
  };

  const handleConvert=async()=>{
    if(!convertTarget)return;
    if(!selectedSupplier)return toast.error('Please select a supplier first');
    setConv(true);
    try{
      await purchaseRequestService.convert(convertTarget._id,{supplier:selectedSupplier._id});
      toast.success('Converted to Purchase Order!');setCT(null);setSS(null);fetchData();
    }catch(e){toast.error(e.response?.data?.message||'Failed');}finally{setConv(false);}
  };

  const [deleteReqTarget, setDeleteReqTarget] = useState(null);
  const [deletingReq, setDeletingReq] = useState(false);

  const handleDeleteReq = async () => {
    setDeletingReq(true);
    try {
      await purchaseRequestService.delete(deleteReqTarget._id);
      toast.success('Request deleted');
      setDeleteReqTarget(null); fetchData();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setDeletingReq(false); }
  };

  const handleCancel=async()=>{setCanc(true);try{await purchaseRequestService.cancel(cancelTarget._id);toast.success('Cancelled');setCAT(null);fetchData();}catch{toast.error('Failed');}finally{setCanc(false);}};

  return(
    <div>
      <Breadcrumb/>
      <PageHeader title="Purchase Requests" subtitle="Draft requests and auto-generate from low stock"
        actions={<div className="flex gap-2">
          <Button variant="outline" onClick={fetchOOS} disabled={oosLoading}><RefreshCw size={15} className={oosLoading?'animate-spin':''}/> Refresh</Button>
          {outOfStock.length>0&&<Button variant="outline" onClick={addOOSToRequest} className="border-orange-300 text-orange-700 hover:bg-orange-50"><AlertTriangle size={15}/> {outOfStock.length} Out of Stock → Request</Button>}
          <Button onClick={()=>{setItems([{medicineName:'',medicineId:'',requestedQty:1,unit:'Piece',notes:''}]);reset();setFormOpen(true);}}><Plus size={16}/> New Request</Button>
        </div>}
      />

      {outOfStock.length>0&&(
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-4">
          <div className="flex items-center gap-2 mb-2"><AlertTriangle size={16} className="text-orange-600"/><p className="text-sm font-semibold text-orange-800">{outOfStock.length} medicines out of stock</p></div>
          <div className="flex flex-wrap gap-2">{outOfStock.slice(0,8).map(i=><span key={i._id} className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full">{i.medicine?.commercialName}</span>)}{outOfStock.length>8&&<span className="text-xs text-orange-500">+{outOfStock.length-8} more</span>}</div>
        </div>
      )}

      <div className="flex gap-2 mb-4">{['','draft','pending','converted','cancelled'].map(s=><button key={s||'all'} onClick={()=>{setFS(s);setPage(1);}} className={`px-3 py-1.5 rounded-lg text-sm font-medium capitalize ${fStatus===s?'bg-blue-600 text-white':'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>{s||'All'}</button>)}</div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-100"><tr>
            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Request #</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Items</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Priority</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Status</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Date</th>
            <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500">Actions</th>
          </tr></thead>
          <tbody className="divide-y divide-gray-50">
            {loading?Array(5).fill(0).map((_,i)=><tr key={i}><td colSpan={6} className="px-4 py-3"><div className="h-4 bg-gray-100 rounded animate-pulse"/></td></tr>):
              data.length===0?<tr><td colSpan={6} className="text-center py-10 text-gray-400">No requests</td></tr>:
              data.map(row=>(
                <tr key={row._id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs text-blue-700">{row.requestNumber}</td>
                  <td className="px-4 py-3"><p className="text-sm font-medium">{row.items?.length} item(s)</p><p className="text-xs text-gray-400">{row.items?.slice(0,2).map(i=>i.medicineName).join(', ')}{row.items?.length>2?'...':''}</p></td>
                  <td className="px-4 py-3"><Badge color={row.priority==='urgent'?'red':row.priority==='high'?'orange':row.priority==='medium'?'blue':'gray'}>{row.priority}</Badge></td>
                  <td className="px-4 py-3"><Badge color={statusColor[row.status]||'gray'}>{row.status}</Badge></td>
                  <td className="px-4 py-3 text-xs text-gray-400">{toJalali(row.createdAt)}</td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex gap-1 justify-center">
                      {!['converted','cancelled'].includes(row.status)&&<>
                        <button onClick={()=>{setCT(row);setSS(null);}} className="flex items-center gap-1 px-2 py-1.5 text-xs rounded-lg bg-green-50 text-green-700 hover:bg-green-100 font-medium"><ArrowRight size={12}/> Convert to PO</button>
                        <button onClick={()=>setCAT(row)} className="px-2 py-1.5 text-xs rounded-lg bg-red-50 text-red-700 hover:bg-red-100 font-medium">Cancel</button>
                      </>}
                      {row.convertedTo&&<span className="text-xs text-gray-400">PO created</span>}
                      {row.status !== 'converted' && (
                        <button onClick={()=>setDeleteReqTarget(row)} className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-600" title="Delete"><Trash2 size={13}/></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            }
          </tbody>
        </table>
        {total>20&&<div className="flex justify-between items-center px-4 py-3 border-t border-gray-100"><p className="text-xs text-gray-500">{((page-1)*20)+1}–{Math.min(page*20,total)} of {total}</p><div className="flex gap-1"><button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1} className="px-3 py-1.5 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">Prev</button><button onClick={()=>setPage(p=>p+1)} disabled={page*20>=total} className="px-3 py-1.5 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">Next</button></div></div>}
      </div>

      {/* Create Modal */}
      <Modal isOpen={formOpen} onClose={()=>setFormOpen(false)} title="New Purchase Request" size="lg">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Priority">
              <Select {...register('priority')}>
                <option value="low">Low</option><option value="medium">Medium</option>
                <option value="high">High</option><option value="urgent">Urgent</option>
              </Select>
            </FormField>
            <FormField label="Notes"><Input {...register('notes')} placeholder="Notes"/></FormField>
          </div>

          <div>
            <div className="flex justify-between items-center mb-2">
              <p className="text-sm font-semibold text-gray-700">Medicines</p>
              <button type="button" onClick={addItem} className="flex items-center gap-1 text-xs px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100"><Plus size={12}/> Add Row</button>
            </div>
            {/* Search to add */}
            <div className="mb-2"><MedicineLookupInline onAdd={addFromSearch}/></div>
            {/* Items table */}
            {items.length>0&&(
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-100"><tr>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-400">Medicine Name</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-400 w-20">Qty</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-400 w-28">Unit</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-400">Notes</th>
                    <th className="w-8"/>
                  </tr></thead>
                  <tbody className="divide-y divide-gray-50">
                    {items.map((item,idx)=>(
                      <tr key={idx}>
                        <td className="px-3 py-2">
                          <input value={item.medicineName} onChange={e=>updateItem(idx,'medicineName',e.target.value)}
                            placeholder="Medicine name" className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"/>
                        </td>
                        <td className="px-3 py-2">
                          <input type="number" min="1" value={item.requestedQty} onChange={e=>updateItem(idx,'requestedQty',Number(e.target.value))}
                            className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none text-center"/>
                        </td>
                        <td className="px-3 py-2">
                          <select value={item.unit} onChange={e=>updateItem(idx,'unit',e.target.value)}
                            className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none bg-white">
                            {UNITS.map(u=><option key={u} value={u}>{u}</option>)}
                          </select>
                        </td>
                        <td className="px-3 py-2">
                          <input value={item.notes||''} onChange={e=>updateItem(idx,'notes',e.target.value)}
                            placeholder="Notes" className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none"/>
                        </td>
                        <td className="px-3 py-2"><button type="button" onClick={()=>removeItem(idx)} className="text-red-400 hover:text-red-600">✕</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" type="button" onClick={()=>setFormOpen(false)}>Cancel</Button>
            <Button type="submit" loading={saving}>Create Request</Button>
          </div>
        </form>
      </Modal>

      {/* Convert Modal */}
      <Modal isOpen={!!convertTarget} onClose={()=>setCT(null)} title="Convert to Purchase Order" size="md">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">Select a supplier to create a Purchase Order.</p>
          <FormField label="Supplier" required>
            <SupplierLookup selected={selectedSupplier} onSelect={setSS} onClear={()=>setSS(null)}/>
          </FormField>
          {!selectedSupplier&&<p className="text-xs text-red-500">⚠️ Supplier is required to convert</p>}
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={()=>setCT(null)}>Cancel</Button>
            <Button onClick={handleConvert} loading={converting} disabled={!selectedSupplier}><ArrowRight size={15}/> Convert to PO</Button>
          </div>
        </div>
      </Modal>

      <ConfirmModal isOpen={!!deleteReqTarget} onClose={()=>setDeleteReqTarget(null)}
        onConfirm={handleDeleteReq} loading={deletingReq}
        title="Delete Request" message={`Permanently delete "${deleteReqTarget?.requestNumber}"?`} confirmLabel="Delete"/>

      <ConfirmModal isOpen={!!cancelTarget} onClose={()=>setCAT(null)} onConfirm={handleCancel} loading={cancelling}
        title="Cancel Request" message={`Cancel request "${cancelTarget?.requestNumber}"?`} confirmLabel="Cancel Request"/>
    </div>
  );
}
